import { releaseStepsToStepRunGraph } from './../helpers/demoHelpers';
import {
    AppPlatform,
    CompletedReleaseSummary,
    Release,
    ReleaseStatus,
    ReleaseStep,
    ReleaseStepRollbackBuildData,
    RollbackResigningStrategy,
} from '../types';
import { RecursiveStringifyEnums } from './buildDistro';

const rollbackStepBuildData: (
    completedReleaseSummary: RecursiveStringifyEnums<CompletedReleaseSummary>,
) => RecursiveStringifyEnums<ReleaseStepRollbackBuildData> = (completedReleaseSummary) => ({
    canBeSubmitted: true,
    betaBuild: {
        appStoreBuild: {
            appStoreBuild: {
                buildNumber: '108602',
                id: '108602',
                integrationId: 'google-beta',
                isProcessed: true,
                type: 'android-aab',
                uploadedAt: '2023-11-16T03:09:28.532323692Z',
            },
            associatedCIBuild: null,
            stabilityInfos: {},
        },
        betaBuildDetails: {
            status: 'draft',
            url: 'https://play.google.com/console/u/0/developers/12345678901234567890/app/12345678901234567890/track/12345678901234567890',
            userFraction: 0.01,
        },
        betaGroups: [],
        betaStatus: 'not-testing',
        betaTesters: [],
        betaTestingNotes: [
            {
                displayLocale: 'Australian English',
                id: '1.0.1:en-AU',
                locale: 'en-AU',
                notes: '',
            },
            {
                displayLocale: 'American English',
                id: '1.0.1:en-US',
                locale: 'en-US',
                notes: '',
            },
            {
                displayLocale: 'Latin American Spanish',
                id: '1.0.1:es-419',
                locale: 'es-419',
                notes: '',
            },
            {
                displayLocale: 'European Spanish',
                id: '1.0.1:es-ES',
                locale: 'es-ES',
                notes: '',
            },
        ],
        buildDistroBuild: null,
        isConsideredExternal: false,
        isLatestBuild: true,
        isSelectedBuild: true,
        selectedReason: 'autoRunwaySelection',
        testTrack: 'alpha',
    },
    betaGroups: null,
    betaTesters: null,
    build: {
        artifacts: [
            {
                appStoreInfos: {
                    'apple:12345': {
                        ascTransactionId: null,
                        createdAt: '2023-11-16T03:08:37.324213145Z',
                        destinationInstallationId: 'appStore',
                        errorMessage: null,
                        trackName: null,
                        uploadCompletedAt: '2023-11-16T03:08:49.998900127Z',
                        uploadRetryCount: 1,
                        uploadStatus: 'success',
                    },
                    'apple-beta:12345': {
                        ascTransactionId: null,
                        createdAt: '2023-11-16T03:08:37.324213145Z',
                        destinationInstallationId: 'appStore',
                        errorMessage: null,
                        trackName: null,
                        uploadCompletedAt: '2023-11-16T03:08:49.998900127Z',
                        uploadRetryCount: 1,
                        uploadStatus: 'success',
                    },
                },
                artifactId: 'ee5db8b9-89de-4416-a5d5-26aa3a44f94a',
                binaryBuildDetails: {
                    appName: 'Apollo',
                    bundleName: 'com.runway.example',
                    artifactType: 'BuildArtifactIOSDevelopment',
                    buildIdentifier: '108602',
                    provisioningProfile: null,
                    versionString: '1.0.1',
                },
                downloadUrl: '',
                dsymUploadInfo: null,
                fileName: 'app-release-bitrise-signed',
                size: 2 * 1_024 * 1_024,
                version: '1.0.1',
                workflowRunId: '',
            },
        ],
        associatedAppStoreBuild: {
            buildNumber: '108602',
            id: '108602',
            integrationId: 'google',
            isProcessed: true,
            type: 'android-aab',
            uploadedAt: '2023-11-16T03:09:28.532323692Z',
        },
        finishedAt: '2023-11-16T03:08:33.87437Z',
        startedAt: '2023-11-16T03:08:32.211357286Z',
        status: 'success',
        testerNotes: {
            displayLocale: 'Australian English',
            id: '1.0.1:en-AU',
            locale: 'en-AU',
            notes: '',
        },
    },
    resignSequenceState: 'Complete',
    sourceVersion: {
        completedReleaseSummary,
        version: '1.0.0',
    },
    subtext: null,
    ciBuild: undefined,
    workflowData: undefined,
    strategy: RollbackResigningStrategy.LocalResign,
});

export const generateRollbackSteps = (
    completedReleaseSummary: RecursiveStringifyEnums<CompletedReleaseSummary>,
): RecursiveStringifyEnums<ReleaseStep>[] => [
    {
        type: 'rollbackBuild',
        id: '1.0.0#rollbackBuild',
        description: 'Rollback build',
        status: 'ready',
        statusReasonString: 'Rollback build has been generated and re-signed.',
        data: rollbackStepBuildData(completedReleaseSummary),
        groupIndex: 0,
        orderIndex: 0,
        checklistItems: [],
        automationRuns: [
            {
                name: 'Re-sign build for rollbacks',
                id: 'AutoResignRollbackBuild',
                stepConfigId: null,
                type: 'AutoResignRollbackBuild',
                description:
                    "Runway will take the build from the source version, bump the version, update the build number, and re-sign it so it's ready for app store upload.",
                relevantPRs: [],

                scheduledAt: null,
                status: 'success',
                statusMessage: null,
                statusMessageLinkUrl: null,
                isPartOfSequence: true,
                sequenceNumber: 0,
                isLifecycle: false,
                autoScheduledFromCadence: null,
                hasEditableSettings: false,
            },
            {
                name: 'Upload build artifacts for distribution',
                id: 'AutoUploadBinaryArtifactToAppStore',
                stepConfigId: null,
                type: 'AutoUploadBinaryArtifactToAppStore',
                description: 'Runway will upload successfully re-signed builds to the Play Console.',
                relevantPRs: [],

                scheduledAt: null,
                status: 'success',
                statusMessage: null,
                statusMessageLinkUrl: null,
                isPartOfSequence: true,
                sequenceNumber: 1,
                isLifecycle: false,
                autoScheduledFromCadence: null,
                hasEditableSettings: false,
            },
        ],
        blockingConfig: 'Blocking',
    },
    {
        type: 'screenshots',
        id: '1.0.0#screenshots',
        description: 'Screenshots',
        status: 'ready',
        statusReasonString:
            'Screenshots are approved and ready. The screenshots from your 1.0.0 release will be used for this rollback. Screenshots will be applied on the Play Console as soon as your rollback is released.',
        data: {
            isApproved: true,
            requiresApproval: false,
            screenshotLocalizations: [
                {
                    displayLocale: 'Australian English',
                    locale: 'en-AU',
                    screenshotSets: [
                        {
                            id: '',
                            screenshotDisplayType: 'phoneScreenshots',
                            screenshots: [
                                {
                                    height: 0,
                                    id: '9621385689824682705',
                                    uploadError: null,
                                    url: 'https://lh3.googleusercontent.com/P-1BeMP1OhfelxxekaZCpu9uTdEFvnm0g8g_knycvz0uenGvjtUuLtO2naIcw4l5sOQ',
                                    width: 0,
                                },
                                {
                                    height: 0,
                                    id: '14478220661696854955',
                                    uploadError: null,
                                    url: 'https://lh3.googleusercontent.com/2LyobJZ6N1KNRngwOinapHBcznd16rG0abkgcuJj4VeZkj8G3B3lFwOq5cbMkQRho1c',
                                    width: 0,
                                },
                            ],
                        },
                        {
                            id: '',
                            screenshotDisplayType: 'sevenInchScreenshots',
                            screenshots: [
                                {
                                    height: 0,
                                    id: '14602541037851514182',
                                    uploadError: null,
                                    url: 'https://lh3.googleusercontent.com/O7deLWzg7pGedO4XeOH7alsk34NSfr_mFtaj6rEsir5I0yWuAlDBcG_uP1vHds7Bg5M',
                                    width: 0,
                                },
                            ],
                        },
                        {
                            id: '',
                            screenshotDisplayType: 'tenInchScreenshots',
                            screenshots: [
                                {
                                    height: 0,
                                    id: '228232527107218878',
                                    uploadError: null,
                                    url: 'https://lh3.googleusercontent.com/074_2ax7YJXOLYyfSNEW7S2KFGwI37TYIsQh-zqnGCwtnGUY2pUsZkUKKGqd4zXnT2w',
                                    width: 0,
                                },
                            ],
                        },
                    ],
                },
                {
                    displayLocale: 'American English',
                    locale: 'en-US',
                    screenshotSets: [
                        {
                            id: '',
                            screenshotDisplayType: 'phoneScreenshots',
                            screenshots: [
                                {
                                    height: 0,
                                    id: '9703556504351862683',
                                    uploadError: null,
                                    url: 'https://lh3.googleusercontent.com/pMjWyNuFkAEttvEuEdtVJ7qmAbhXgCVP4pqYgNuxoJETcmcVhI9Gw32Pikgl6mB5xg',
                                    width: 0,
                                },
                            ],
                        },
                        {
                            id: '',
                            screenshotDisplayType: 'sevenInchScreenshots',
                            screenshots: [
                                {
                                    height: 0,
                                    id: '13124701049672169311',
                                    uploadError: null,
                                    url: 'https://lh3.googleusercontent.com/BNZoj7tG-SpxCoWSBObASKrlYd_9sud1CPYcHbCnHYYsJ2IBzl0I-7mK75vx4jwxqQo',
                                    width: 0,
                                },
                            ],
                        },
                        {
                            id: '',
                            screenshotDisplayType: 'tenInchScreenshots',
                            screenshots: [
                                {
                                    height: 0,
                                    id: '9005868707608413157',
                                    uploadError: null,
                                    url: 'https://lh3.googleusercontent.com/hWOgXtB7yIo4Yu53tIl4BiGwSZ_K3pVKzHJllOT2D9BJ_FwA3nKRTQ8wEIzN--0BxWM',
                                    width: 0,
                                },
                            ],
                        },
                    ],
                },
                {
                    displayLocale: 'Latin American Spanish',
                    locale: 'es-419',
                    screenshotSets: [
                        {
                            id: '',
                            screenshotDisplayType: 'phoneScreenshots',
                            screenshots: [
                                {
                                    height: 0,
                                    id: '16694042289234303662',
                                    uploadError: null,
                                    url: 'https://lh3.googleusercontent.com/ILhWuDHNwsxMIPyAjeXs1xIwl9TVqF8dfC95-NdlCXaxPjD6kvEA8wwXK6EJzvnUWlM',
                                    width: 0,
                                },
                                {
                                    height: 0,
                                    id: '13574830087363125373',
                                    uploadError: null,
                                    url: 'https://lh3.googleusercontent.com/D3gxVMTwL2PK49jTqmJD-uTRmj4oLXO1ZgD8Qu8fMK8lNK2O2VzSxXK3f8Ff3dEtHQ',
                                    width: 0,
                                },
                            ],
                        },
                        {
                            id: '',
                            screenshotDisplayType: 'sevenInchScreenshots',
                            screenshots: [
                                {
                                    height: 0,
                                    id: '12387271587435637567',
                                    uploadError: null,
                                    url: 'https://lh3.googleusercontent.com/rLcmRacJD2S50uiL5TI104JjAfJnKjURqJrwk7U9b96eFCzuXCT2JIcTD7PgF2gW_j8',
                                    width: 0,
                                },
                            ],
                        },
                        {
                            id: '',
                            screenshotDisplayType: 'tenInchScreenshots',
                            screenshots: [
                                {
                                    height: 0,
                                    id: '14496421212826963514',
                                    uploadError: null,
                                    url: 'https://lh3.googleusercontent.com/sSwuIibaxJP-tSARi6bFmwlIo_kSJ9sUismNcVW_BPPePjvFy_2PqUkZGvN1oOKO3UdW',
                                    width: 0,
                                },
                            ],
                        },
                    ],
                },
                {
                    displayLocale: 'European Spanish',
                    locale: 'es-ES',
                    screenshotSets: [
                        {
                            id: '',
                            screenshotDisplayType: 'phoneScreenshots',
                            screenshots: [
                                {
                                    height: 0,
                                    id: '8936128226127154221',
                                    uploadError: null,
                                    url: 'https://lh3.googleusercontent.com/p8pdbvBnW7UAsPJqFHGuUW-s141m2GLHz2a3q7n5KJYXM6vdQf6xBN5hzIFtK9OAXSo',
                                    width: 0,
                                },
                                {
                                    height: 0,
                                    id: '5201899023591544336',
                                    uploadError: null,
                                    url: 'https://lh3.googleusercontent.com/ai-odrkaastf-ZwzGN7BDL_p0U4Mchlu2XBEVz1CgK6PU8Pjji2wSBkGxzxQck-OrcXB',
                                    width: 0,
                                },
                            ],
                        },
                        {
                            id: '',
                            screenshotDisplayType: 'sevenInchScreenshots',
                            screenshots: [
                                {
                                    height: 0,
                                    id: '9862455254781547037',
                                    uploadError: null,
                                    url: 'https://lh3.googleusercontent.com/LDrKYN6uoeLPhr5iIcAbyCgI-NGLrmfTmSLfqa9UG3k5LRGsltyfZvC3fI2MQzvNqbKI',
                                    width: 0,
                                },
                            ],
                        },
                        {
                            id: '',
                            screenshotDisplayType: 'tenInchScreenshots',
                            screenshots: [
                                {
                                    height: 0,
                                    id: '6709998350126413443',
                                    uploadError: null,
                                    url: 'https://lh3.googleusercontent.com/xRSf3NVyi3oAwZRk50VXaRgmbaq0FERCBkKPQQwc9FeLbaVIe1-OaTrDQiFYwmFNtyY3',
                                    width: 0,
                                },
                            ],
                        },
                    ],
                },
            ],
            storeUrl: null,
        },
        groupIndex: 2,
        orderIndex: 0,
        checklistItems: [],
        automationRuns: [],
        blockingConfig: 'Blocking',
    },
    {
        type: 'metadata',
        id: '1.0.0#metadata',
        description: 'Metadata',
        status: 'ready',
        statusReasonString:
            "Metadata is approved and ready. 'What's new' notes from your 1.0.0 release will be used for this rollback. 'What's new' notes will be applied as soon as the version is created in Google Play.",
        data: {
            isApproved: true,
            localizations: [
                {
                    description: '',
                    displayLocale: 'Australian English',
                    id: '1.0.0:en-AU',
                    keywords: '',
                    locale: 'en-AU',
                    promotionalText: null,
                    shortDescription: 'A small test and development app for the Runway team.',
                    status: 'ready',
                    whatsNew: 'Default for.. Australian English',
                },
                {
                    description: '',
                    displayLocale: 'American English',
                    id: '1.0.0:en-US',
                    keywords: '',
                    locale: 'en-US',
                    promotionalText: null,
                    shortDescription: 'A small test and development app for the Runway team.',
                    status: 'ready',
                    whatsNew: 'Default for... American English',
                },
                {
                    description: '',
                    displayLocale: 'Latin American Spanish',
                    id: '1.0.0:es-419',
                    keywords: '',
                    locale: 'es-419',
                    promotionalText: null,
                    shortDescription: 'Una pequeña aplicación para el equipo de Runway.',
                    status: 'ready',
                    whatsNew: 'Default for... Latin American Spanish',
                },
                {
                    description: '',
                    displayLocale: 'European Spanish',
                    id: '1.0.0:es-ES',
                    keywords: '',
                    locale: 'es-ES',
                    promotionalText: null,
                    shortDescription: 'Una pequeña aplicación para el equipo de Runway.',
                    status: 'ready',
                    whatsNew: 'Default for... European Spanish',
                },
            ],
            requiresApproval: false,
            storeUrl: null,
        },
        groupIndex: 2,
        orderIndex: 1,
        checklistItems: [],
        automationRuns: [
            {
                name: 'Populate store metadata to new releases',
                id: 'AutoApplyMetadata',
                type: 'AutoApplyMetadata',
                stepConfigId: null,
                description:
                    'Runway will apply release notes to new versions created in the Play Console. You can make updates to the text as needed before you submit your update for review.',
                relevantPRs: [],

                scheduledAt: null,
                status: 'active',
                statusMessage: null,
                statusMessageLinkUrl: null,
                isPartOfSequence: false,
                sequenceNumber: null,
                isLifecycle: false,
                autoScheduledFromCadence: null,
                hasEditableSettings: true,
            },
        ],
        blockingConfig: 'Blocking',
    },
    {
        type: 'approvals',
        id: '1.0.0#approvals',
        description: 'Approvals',
        status: 'ready',
        statusReasonString: 'Step complete.',
        data: {
            approvalItems: [],
        },
        groupIndex: 3,
        orderIndex: 0,
        checklistItems: [],
        automationRuns: [],
        blockingConfig: 'Blocking',
    },
    {
        type: 'submission',
        id: '1.0.0#submission',
        description: 'Submission',
        status: 'pending',
        statusReasonString: 'Submit your rollback build (#108602) for review.',
        data: {
            appStoreData: {
                allInfoPresentForSubmission: true,
                earliestReleaseDate: null,
                exportComplianceInfoUsesEncryption: false,
                integrationId: 'apple',
                phasedRelease: {
                    dayNumber: 0,
                    isManuallyAcceleratedPhasedRollout: false,
                    isRunwayAcceleratedRollout: false,
                    isRunwayPaused: false,
                    startDate: null,
                    state: 'INACTIVE',
                    userFraction: 0,
                },
                releaseRequested: false,
                releaseType: 'MANUAL',
                reviewDetail: {
                    contactEmail: 'buzz@demo.com',
                    contactFirstName: 'Buzz',
                    contactLastName: 'Aldrin',
                    contactPhone: '+1(123)456-7891',
                    demoAccountName: null,
                    demoAccountPassword: null,
                    demoAccountRequired: false,
                    notes: '',
                },
                state: 'PENDING_DEVELOPER_RELEASE',
            },
            appStoreReleaseSettings: {
                initialStagedRolloutPercentage: null,
                stagedRelease: false,
            },
            lastCompletedRelease: {
                createdOrigin: 'draft',
                displayableVersion: '1.0.0',
                id: 'appollo-ios:1.0.0',
                isReleaseTagged: true,
                phasedRelease: null,
                releaseId: '1.0.0',
                releasedAt: '2023-11-15T22:40:37.688431229Z',
                rollbackInfo: null,
                rollbackTargetVersion: null,
                skippedAt: null,
                status: 'completed',
                timelinePhase: 'rollback',
                type: 'minor',
                version: '1.0.0',
            },
            storeUrl: null,
            submittedAt: null,
            workflowData: null,
        },
        groupIndex: 4,
        orderIndex: 0,
        checklistItems: [
            {
                id: 'clae4r9jr926mfmkohng',
                uid: 'clae4r9jr926mfmkoho0',
                title: 'Submission rollbacks',
                description: null,
                type: 'binary',
                statuses: ['approved'],
                releaseStepType: 'submission',
                approverGroups: ['pilot'],
                releaseTypes: 'rollbackOnly',
                notifyEnabled: false,
                oneOffForVersion: null,
                owners: ['ale@runway.team'],
                createdAt: '2023-11-15T17:23:25.552702+02:00',
                status: null,
                comment: null,
                incompleteReminderEnabled: false,
                autocompleteAtTargetCompletionEnabled: false,
                includeDescriptionInNotificationsEnabled: false,
                disableReminderAfterReleaseComplete: false,
                orgTemplateId: '',
            },
        ],
        automationRuns: [
            {
                name: 'Submit app for review',
                type: 'AutoSubmit',
                id: 'AutoSubmit',
                stepConfigId: null,
                description:
                    'Runway will submit your rollback for review as soon as all previous steps are ready and the previous release has gone live.',
                relevantPRs: [],

                scheduledAt: null,
                status: 'inactive',
                statusMessage: 'Auto-submit is disabled for rollback builds',
                statusMessageLinkUrl: null,
                isPartOfSequence: false,
                sequenceNumber: null,
                isLifecycle: true,
                autoScheduledFromCadence: null,
                hasEditableSettings: true,
            },
            {
                name: 'Create and sync draft production release in the Play Console to match current Runway release',
                type: 'AutoCreateAppStoreVersions',
                id: 'AutoCreateAppStoreVersions',
                stepConfigId: null,
                description:
                    'Runway will create a Production track release in the Play Console immediately prior to rollback submission.',
                relevantPRs: [],

                scheduledAt: null,
                status: 'pending',
                statusMessage: null,
                statusMessageLinkUrl: null,
                isPartOfSequence: false,
                sequenceNumber: null,
                isLifecycle: false,
                autoScheduledFromCadence: null,
                hasEditableSettings: true,
            },
            {
                name: 'Add APK/AAB to the production track',
                type: 'AutoSelectLatestBuild',
                id: 'AutoSelectLatestBuild',
                stepConfigId: null,
                description:
                    'Based on your selection below, Runway will select an APK/AAB in the Play Console, and apply it to the Draft release on the production track. You can optionally choose to limit automatic selection to APKs/AABs that are already available on a specific testing track.',
                relevantPRs: [],

                scheduledAt: null,
                status: 'active',
                statusMessage: null,
                statusMessageLinkUrl: null,
                isPartOfSequence: false,
                sequenceNumber: null,
                isLifecycle: false,
                autoScheduledFromCadence: null,
                hasEditableSettings: true,
            },
        ],
        blockingConfig: 'Blocking',
    },
    {
        type: 'storeReview',
        id: '1.0.0#storeReview',
        description: 'App store review',
        status: 'inactive',
        statusReasonString: 'Waiting for Submission to be ready.',
        data: {
            appStoreData: {
                allInfoPresentForSubmission: true,
                earliestReleaseDate: null,
                exportComplianceInfoUsesEncryption: false,
                integrationId: 'apple',
                phasedRelease: {
                    dayNumber: 0,
                    isManuallyAcceleratedPhasedRollout: false,
                    isRunwayAcceleratedRollout: false,
                    isRunwayPaused: false,
                    startDate: null,
                    state: 'INACTIVE',
                    userFraction: 0,
                },
                releaseRequested: false,
                releaseType: 'MANUAL',
                reviewDetail: {
                    contactEmail: 'buzz@demo.com',
                    contactFirstName: 'Buzz',
                    contactLastName: 'Aldrin',
                    contactPhone: '+1(123)456-7891',
                    demoAccountName: null,
                    demoAccountPassword: null,
                    demoAccountRequired: false,
                    notes: '',
                },
                state: 'PENDING_DEVELOPER_RELEASE',
            },
            appStoreReleaseSettings: {
                initialStagedRolloutPercentage: null,
                stagedRelease: false,
            },
            markedRejectedAt: null,
            storeUrl: null,
        },
        groupIndex: 4,
        orderIndex: 1,
        checklistItems: [],
        automationRuns: [],
        blockingConfig: 'Blocking',
    },
    {
        type: 'takeoff',
        id: '1.0.0#takeoff',
        description: 'Release',
        status: 'inactive',
        statusReasonString: 'Waiting for App Store Review to be ready.',
        data: {
            appStoreData: {
                allInfoPresentForSubmission: true,
                earliestReleaseDate: null,
                exportComplianceInfoUsesEncryption: false,
                integrationId: 'apple',
                phasedRelease: null,
                releaseRequested: false,
                releaseType: 'MANUAL',
                reviewDetail: {
                    contactEmail: 'buzz@demo.com',
                    contactFirstName: 'Buzz',
                    contactLastName: 'Aldrin',
                    contactPhone: '+1(123)456-7891',
                    demoAccountName: null,
                    demoAccountPassword: null,
                    demoAccountRequired: false,
                    notes: '',
                },
                state: 'PENDING_DEVELOPER_RELEASE',
            },
            appStoreReleaseSettings: {
                initialStagedRolloutPercentage: null,
                stagedRelease: false,
            },
            appStoreUrl: 'https://play.google.com/store/apps/details?id=com.runway.example',
            storeUrl: null,
            tagData: {
                codeDiffUrl: null,
                expectedReleaseTagName: 'v1.0.1',
                releaseTag: null,
                releaseTagState: 'notTaggable',
            },
        },
        groupIndex: 4,
        orderIndex: 2,
        checklistItems: [],
        automationRuns: [
            {
                name: 'Tag releases at the end of the release cycle',
                type: 'AutoTagReleases',
                id: 'AutoTagReleases',
                stepConfigId: null,
                description:
                    'Runway will tag your production release commit at the end of each release cycle, and generate a summary of all work going out with the release.',
                relevantPRs: [],

                scheduledAt: null,
                status: 'active',
                statusMessage: null,
                statusMessageLinkUrl: null,
                isPartOfSequence: false,
                sequenceNumber: null,
                isLifecycle: false,
                autoScheduledFromCadence: null,
                hasEditableSettings: true,
            },
            {
                name: 'Attach build artifacts to VCS release record when tagging',
                type: 'AutoUploadReleaseAssets',
                id: 'AutoUploadReleaseAssets',
                stepConfigId: null,
                description:
                    'When tagging your production release commit, Runway will attach all artifacts generated from your active production build workflow as assets on your release record.',
                relevantPRs: [],

                scheduledAt: null,
                status: 'inactive',
                statusMessage: null,
                statusMessageLinkUrl: null,
                isPartOfSequence: false,
                sequenceNumber: null,
                isLifecycle: false,
                autoScheduledFromCadence: null,
                hasEditableSettings: true,
            },
            {
                name: 'Mark releases as completed in project management tool',
                type: 'AutoCompleteIssueTrackingReleases',
                id: 'AutoCompleteIssueTrackingReleases',
                stepConfigId: null,
                description:
                    'Runway will mark releases as completed in your project management tool at the end of each release cycle.',
                relevantPRs: [],

                scheduledAt: null,
                status: 'inactive',
                statusMessage: 'Inactive for rollback releases.',
                statusMessageLinkUrl: null,
                isPartOfSequence: false,
                sequenceNumber: null,
                isLifecycle: false,
                autoScheduledFromCadence: null,
                hasEditableSettings: true,
            },
            {
                name: 'Halt unstable phased releases',
                type: 'AutoHaltUnstablePhasedReleases',
                id: 'AutoHaltUnstablePhasedReleases',
                stepConfigId: null,
                description:
                    'Runway will halt your staged rollout if all of your defined conditions have been met, and any of your selected health metrics become unhealthy.',
                relevantPRs: [],

                scheduledAt: null,
                status: 'inactive',
                statusMessage: 'Inactive for rollback releases.',
                statusMessageLinkUrl: null,
                isPartOfSequence: false,
                sequenceNumber: null,
                isLifecycle: false,
                autoScheduledFromCadence: null,
                hasEditableSettings: true,
            },
            {
                name: 'Backmerge changes from the release branch',
                type: 'AutoBackmergeReleaseBranches',
                id: 'AutoBackmergeReleaseBranches',
                stepConfigId: null,
                description:
                    'Runway will backmerge changes from the release branch to the working branch (and any other upcoming release branches).',
                relevantPRs: [],

                scheduledAt: null,
                status: 'inactive',
                statusMessage: null,
                statusMessageLinkUrl: null,
                isPartOfSequence: false,
                sequenceNumber: null,
                isLifecycle: false,
                autoScheduledFromCadence: null,
                hasEditableSettings: true,
            },
            {
                name: 'Release stable phased releases to 100% of users',
                type: 'AutoPromoteStablePhasedReleases',
                id: 'AutoPromoteStablePhasedReleases',
                stepConfigId: null,
                description:
                    'Runway will roll out your update to all users early if all of your defined conditions have been met, and your selected health metrics stay within your defined health threshold',
                relevantPRs: [],

                scheduledAt: null,
                status: 'inactive',
                statusMessage: 'Inactive for rollback releases.',
                statusMessageLinkUrl: null,
                isPartOfSequence: false,
                sequenceNumber: null,
                isLifecycle: false,
                autoScheduledFromCadence: null,
                hasEditableSettings: true,
            },
            {
                name: 'Delete version-specific release branches at the end of the release cycle',
                type: 'AutoDeleteReleaseBranch',
                id: 'AutoDeleteReleaseBranch',
                stepConfigId: null,
                description:
                    'Runway will delete version-specific release branches when the release is complete and tagged.',
                relevantPRs: [],

                scheduledAt: null,
                status: 'inactive',
                statusMessage: 'Inactive for rollback releases.',
                statusMessageLinkUrl: null,
                isPartOfSequence: false,
                sequenceNumber: null,
                isLifecycle: false,
                autoScheduledFromCadence: null,
                hasEditableSettings: true,
            },
            {
                name: 'Update status of tickets upon milestone',
                type: 'AutoTransitionIssueTrackingTickets',
                id: 'AutoTransitionIssueTrackingTickets',
                stepConfigId: null,
                description:
                    'Runway will update tickets associated with the release to the configured status when each configured release milestone is reached.',

                scheduledAt: null,
                status: 'inactive',
                statusMessage: 'Inactive for rollback releases.',
                statusMessageLinkUrl: null,
                isPartOfSequence: false,
                sequenceNumber: null,
                isLifecycle: false,
                autoScheduledFromCadence: null,
                relevantPRs: [],

                hasEditableSettings: true,
            },
            {
                name: 'Distribute release summary to additional recipients at the end of each release cycle',
                type: 'AutoDistributeReleaseSummary',
                id: 'AutoDistributeReleaseSummary',
                stepConfigId: null,
                description:
                    'Runway will distribute a release summary via email at the end of each release cycle. You can configure the default template from App settings > Custom strings, and you can make changes to the release summary for a specific release from the Release step up until the release has gone live.',

                scheduledAt: null,
                status: 'inactive',
                statusMessage: 'Inactive for rollback releases.',
                statusMessageLinkUrl: null,
                isPartOfSequence: false,
                sequenceNumber: null,
                isLifecycle: false,
                autoScheduledFromCadence: null,
                relevantPRs: [],

                hasEditableSettings: true,
            },
            {
                name: 'Prepare rollback releases',
                type: 'AutoPrepareRollbackRelease',
                id: 'AutoPrepareRollbackRelease',
                stepConfigId: null,
                description:
                    'Runway will re-sign, upload and (optionally) submit the previous stable build to have ready in case your latest release needs to be pulled back.',

                scheduledAt: null,
                status: 'inactive',
                statusMessage: 'Rollbacks are not prepared for hotfix or rollback releases.',
                statusMessageLinkUrl: null,
                isPartOfSequence: false,
                sequenceNumber: null,
                isLifecycle: false,
                autoScheduledFromCadence: null,
                relevantPRs: [],

                hasEditableSettings: true,
            },
            {
                name: 'Custom phased / staged rollout',
                type: 'AutoStagedRollout',
                id: 'AutoStagedRollout',
                stepConfigId: null,
                description:
                    'Runway will increase the staged rollout percentage over the course of several days. You can optionally specify blackout dates and times on which your staged rollout will not increment. You must set staged rollout as your default release method in Runway for this automation to work.',

                scheduledAt: null,
                status: 'inactive',
                statusMessage: null,
                statusMessageLinkUrl: null,
                isPartOfSequence: false,
                sequenceNumber: null,
                isLifecycle: false,
                autoScheduledFromCadence: null,
                hasEditableSettings: true,
                relevantPRs: [],
            },
        ],
        blockingConfig: 'Blocking',
    },
];

export const rollbackReleaseMock: (
    platform: AppPlatform,
    completedReleaseSummary: RecursiveStringifyEnums<CompletedReleaseSummary>,
) => RecursiveStringifyEnums<Release> = (platform, completedReleaseSummary) => ({
    id: 'appollo-ios:1.0.1',
    releaseId: '1.0.1',
    releasePilotId: 'ssullenberger@demo.com',
    phasedReleaseHasBeenSupersededByNewRelease: false,
    lifecycleAutomations: [],
    version: '1.0.1',
    reviewSubmissionWithoutAppVersion: false,
    displayableVersion: '1.0.1',
    status: 'active',
    timelinePhase: 'rollback',
    type: 'rollback',
    createdAt: '2023-11-15T18:15:53.434459-05:00',
    updatedAt: '0001-01-01T00:00:00Z',
    completedAt: null,
    targetKickoffDate: null,
    targetSubmitDate: null,
    targetReleaseDate: null,
    stepRunGraph: releaseStepsToStepRunGraph(generateRollbackSteps(completedReleaseSummary)),
    steps: generateRollbackSteps(completedReleaseSummary),
    latestStagingBuild: null,
    latestWorkingBranchBuild: null,
    latestSuccessfulBuild: null,
    selectedStagingBuild: null,
    prodBuild: null,
    latestSuccessfulProdBuild: null,
    workflowRunId: null,
    appStoreSelectedBuilds: [
        {
            appStoreBuild: {
                id: '108602',
                buildNumber: '108602',
                uploadedAt: '2023-11-16T03:09:28.532323692Z',
                integrationId: 'google',
                type: 'android-aab',
                isProcessed: true,
            },
            associatedCIBuild: null,
            isCurrentlySelected: true,
            isNewestAppStoreBuild: true,
            stabilityInfos: {},
            selectedBuildReason: 'rollback',
            isRecentBuild: false,
            tracks: null,
            autoSelectedFromTrack: null,
        },
    ],
    appStoreAvailableBuilds: [
        {
            appStoreBuild: {
                id: '108602',
                buildNumber: '108602',
                uploadedAt: '2023-11-16T03:09:28.532323692Z',
                integrationId: 'google',
                type: 'android-aab',
                isProcessed: true,
            },
            associatedCIBuild: null,
            isCurrentlySelected: false,
            isNewestAppStoreBuild: true,
            stabilityInfos: {},
            selectedBuildReason: null,
            isRecentBuild: true,
            tracks: ['beta'],
            autoSelectedFromTrack: null,
        },
    ],
    pendingStepChecklistItemCounts: {
        approvals: 0,
        betaTesting: 0,
        featureReadiness: 0,
        kickoff: 0,
        metadata: 0,
        regressionTesting: 0,
        releaseCandidate: 0,
        screenshots: 0,
        storeReview: 0,
        submission: 1,
        takeoff: 0,
    },
    phasedRelease: null,
    releasePlanningSummary: null,
    releaseSummaryMessage: {
        message:
            "{appName} ({appPlatform}) release {version} was successfully released on {releasedAt}! 🎉\n\nHere are some highlights from this release:\n✅ {contributorsCount} items of work were completed ({issuesCount} tickets, {commitsCount} commits)\n🤝 {contributorsCount} engineers contributed code to this release\n⌨️ {linesOfCodeCount} lines of code were changed\n🔃 This release cycle lasted {releaseDuration} and was piloted by {releasePilot} 👩\u200d✈️\n🗒️ What's new:\n{releaseNotes}\n{releaseWorkItemsLink}\n{releaseAppStoreLink}\n{releaseVCSReleaseLink}",
    },
    releaseBranch: 'release-1.0.1',
    workingBranch: 'development',
    timelineEventsHistory: [
        {
            type: 'release.created',
            triggeredAt: '2023-05-20T19:48:48.686257148Z',
            triggeredBy: 'runway-admin',
            userId: 'ssullenberger@demo.com',
            status: 'completed',
            eventData: null,
            orgId: 'appollo-ios',
            appId: 'appollo-ios',
            releaseStepArchetypes: [],
            releaseId: 'appollo-ios:1.0.1',
            releaseData: {
                version: '1.0.1',
                displayableVersion: '1.0.1',
            },
            destinationStepConfigIds: [],
            previouslySeenWithErrorAt: [],
            previouslySeenCount: 0,
        },
    ],
    doesBranchExist: false,
    isStaticReleaseBranch: false,
    releaseBranchCreatedOn: null,
    releaseFAFoundOn: null,
    releaseCommitAfterTagFoundOn: null,
    kickedOffAt: null,
    submittedAt: null,
    startedReviewOn: null,
    approvedOn: null,
    storeRejectedOn: null,
    devRejectedOn: null,
    releasedAt: null,
    createdOrigin: 'draft',
    rollbackTargetVersionInfo: {
        releaseId: '1.1.0',
        version: '1.1.0',
        status: ReleaseStatus.Active,
    },
    isKickedOff: false,
    nextVersion: '1.1.0',
    displayableNextVersion: '1.0.1',
    scheduledAutomationsPaused: null,
    isReleaseTagged: false,
    isSubmitted: false,
    canBeSubmitted: true,
    isReleased: false,
    appStoreUpdatesRequireSubmitForReview: false,
    overview: {
        codeContributors: [],
        topChangedFiles: [],
        contributingTeams: [],
        totalFilesChanged: 0,
        averageAppStoreCrashFreeUsers: 0,
        averageAppStoreCrashFreeSessions: 0,
        averageBetaCrashFreeUsers: 0,
        averageBetaCrashFreeSessions: 0,
        totalAutomationsPerformedCount: 6,
        estimatedAutomationsTimeSaved: 13860,
        estimatedAutomationsCostSaving: 32813,
        releaseConfidenceScore: {
            value: 0.1983778804600115,
            displayString: '19.84/100 (Higher more likely to fail)',
            updatedAt: '2023-11-21T23:30:12.11191444Z',
        },
        totalAutomationsScheduledCount: 0,
    },
    appStoreReviewData: {
        averageRating: 0,
        totalRatingsCount: 0,
        recentReviews: [],
        issues: [],
        viewAllReviewsUrl:
            'https://play.google.com/store/apps/details?id=com.runway.example&hl=en&gl=US&showAllReviews=true',
    },
    selectedBetaBuild: {
        metrics: {
            numInstalls: 0,
        },
        appStoreBuild: {
            appStoreBuild: {
                id: '108602',
                buildNumber: '108602',
                uploadedAt: '2023-11-16T03:09:28.532323692Z',
                integrationId: 'google-beta',
                type: 'android-aab',
                isProcessed: true,
            },
            associatedCIBuild: null,
            isCurrentlySelected: false,
            isNewestAppStoreBuild: false,
            stabilityInfos: {},
            selectedBuildReason: null,
            isRecentBuild: false,
            tracks: null,
            autoSelectedFromTrack: null,
        },
        betaStatus: 'not-testing',
        testTrack: 'alpha',
        betaGroups: [],
        betaTesters: [],
        betaTestingNotes: [
            {
                id: '1.0.1:en-AU',
                locale: 'en-AU',
                displayLocale: 'Australian English',
                notes: '',
            },
            {
                id: '1.0.1:en-US',
                locale: 'en-US',
                displayLocale: 'American English',
                notes: '',
            },
            {
                id: '1.0.1:es-419',
                locale: 'es-419',
                displayLocale: 'Latin American Spanish',
                notes: '',
            },
            {
                id: '1.0.1:es-ES',
                locale: 'es-ES',
                displayLocale: 'European Spanish',
                notes: '',
            },
        ],
        isConsideredExternal: false,
        isSelectedBuild: true,
        selectedReason: 'autoRunwaySelection',
        isLatestBuild: true,
        betaBuildDetails: {
            status: 'draft',
            url: '',
        },
        buildDistroBuild: null,
    },
    runwayOpenedPullRequests: [],
    rolloutStartedAt: null,
    releaseName: null,
    skippedAt: null,
    sourceVersion: '1.0.0',
    rollbackInfo: null,
    shouldWarnAboutNewerCommits: false,
    shouldMatchPreviousPhasedRelease: false,
    RenderedAt: '2023-11-22T11:30:12.190890071Z',
    destinationSummaries: [],
});
